import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Stethoscope,
  CreditCard,
  Settings,
  LogOut,
  FileText,
  X,
  UserCircle,
  Bed,
  FlaskConical,
  Pill,
  Package,
  Briefcase,
  Shield,
  ClipboardList,
  Bell,
  Activity,
  HeartPulse,
  Building2,
  Wallet,
  ChevronDown,
  ChevronRight,
  PlusCircle,
  List,
  AlertTriangle,
  Tags,
  Tag,
  Award,
  LayoutGrid,
  CalendarDays,
  Receipt,
  DollarSign,
  BookOpen,
  Building,
  DoorOpen,
  BarChart3,
  Truck,
  ShoppingCart,
  PackageCheck,
  ScrollText,
  Monitor,
  ScanBarcode,
  Database,
  Terminal,
  Globe,
  Phone,
  FolderTree,
  Images,
  MessageSquareQuote,
  BadgeDollarSign,
  HandCoins,
  Handshake,
  SlidersHorizontal,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { usePermissions, UserRole } from '../../hooks/usePermissions';
import { ThemeToggle } from '../ui/theme-toggle';
import { useState } from 'react';

interface SidebarProps {
  user: { name: string; roles: string[] } | null;
  onLogout: () => void;
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  path?: string;
  icon: React.ElementType;
  label: string;
  /** Optional sub-heading shown above this item and its following siblings. */
  group?: string;
  children?: NavItem[];
}

interface NavSection {
  /** Optional group heading. Sections without a title render without one. */
  title?: string;
  items: NavItem[];
}

function isActivePath(location: string, path: string) {
  return location === path || location.startsWith(`${path}/`);
}

function pathMatchesAllowed(path: string, allowedPaths: string[]): boolean {
  return allowedPaths.some((allowed) => {
    if (allowed === path) return true;
    if (allowed.endsWith('/*')) {
      const prefix = allowed.slice(0, -1);
      return path.startsWith(prefix);
    }
    return false;
  });
}

function hasAllowedDescendant(item: NavItem, allowedPaths: string[]): boolean {
  if (item.path && pathMatchesAllowed(item.path, allowedPaths)) return true;
  if (item.children) {
    return item.children.some((c) => hasAllowedDescendant(c, allowedPaths));
  }
  return false;
}

function hasActiveDescendant(item: NavItem, location: string): boolean {
  if (item.children) {
    return item.children.some(
      (c) =>
        // An index child (same path as its parent) only counts when we are
        // exactly on it, so a parent like `/billing` is not treated as active
        // while the user is on a sibling such as `/billing/expenses`.
        (c.path === item.path ? location === c.path : isActivePath(location, c.path || '')) ||
        hasActiveDescendant(c, location),
    );
  }
  return false;
}

/**
 * Returns the label of the deepest top-level nav item matching `location`.
 *
 * Used so that a parent only highlights when the location really belongs to it
 * rather than to one of its siblings. For example, `/billing/expenses` matches
 * both `/billing` and `/billing/expenses`; the longer match (Expenses) wins, so
 * the Billing entry is not highlighted on an Expenses page.
 */
function findActiveTopLevelLabel(sections: NavSection[], location: string): string | undefined {
  let bestLabel: string | undefined;
  let bestLength = -1;
  for (const section of sections) {
    for (const item of section.items) {
      if (!item.path) continue;
      if (isActivePath(location, item.path) && item.path.length > bestLength) {
        bestLength = item.path.length;
        bestLabel = item.label;
      }
    }
  }
  return bestLabel;
}

/** Routes that only the environment super admin may see in the sidebar. */
const SUPER_ADMIN_ONLY_PATHS = new Set(['/settings/developer']);

/**
 * Navigation is grouped by domain and ordered by the primary clinical
 * workflow, then support functions, ending with system configuration.
 * Within each group, operational screens come before their master data.
 */
const staticNavSections: NavSection[] = [
  {
    items: [{ path: '/', icon: LayoutDashboard, label: 'Dashboard' }],
  },
  {
    title: 'Clinical',
    items: [
      {
        path: '/patients',
        icon: Users,
        label: 'Patients',
        children: [
          { path: '/patients', label: 'All Patients', icon: List, group: 'Patients' },
          { path: '/patients/new', label: 'Add Patient', icon: PlusCircle },
          { path: '/patients/clinical-alerts', label: 'Clinical Alerts', icon: AlertTriangle },
          { path: '/patients/categories', label: 'Categories', icon: Tags, group: 'Configuration' },
        ],
      },
      {
        path: '/opd',
        icon: Stethoscope,
        label: 'OPD',
        children: [
          { path: '/appointments', label: 'Appointments', icon: CalendarDays },
          { path: '/opd/diagnostic-receipts', label: 'Receipts', icon: ClipboardList },
          { path: '/opd/diagnostic-receipts/new', label: 'New Receipt', icon: PlusCircle },
        ],
      },
      {
        path: '/ipd',
        icon: Bed,
        label: 'IPD',
        children: [
          { path: '/ipd', label: 'Admissions', icon: List, group: 'Patient Care' },
          { path: '/ipd/occupancy', label: 'Bed Occ.', icon: BarChart3 },
          { path: '/ipd/vitals', label: 'Vitals', icon: HeartPulse },
          { path: '/wards', label: 'Wards', icon: Building2, group: 'Facility' },
          { path: '/beds', label: 'Beds', icon: Bed },
        ],
      },
      {
        path: '/administration',
        icon: FlaskConical,
        label: 'Diagnostics',
        children: [
          { path: '/administration/orders', label: 'Orders', icon: ClipboardList, group: 'Workflow' },
          { path: '/diagnostics/collection', label: 'Collection', icon: ScanBarcode },
          { path: '/lab/receiving', label: 'Lab Receiving', icon: PackageCheck },
          { path: '/lab/workbench', label: 'Lab Bench', icon: FlaskConical },
          { path: '/administration/diagnostic-tests', label: 'Tests', icon: List, group: 'Configuration' },
          { path: '/administration/services', label: 'Services', icon: Tags },
          { path: '/administration/service-types', label: 'Service Types', icon: Tag },
          { path: '/administration/diagnostic-accessories', label: 'Accessories', icon: Package },
          { path: '/administration/diagnostic-categories', label: 'Categories', icon: LayoutGrid },
          { path: '/administration/specimen-types', label: 'Specimen Types', icon: FlaskConical },
        ],
      },
    ],
  },
  {
    title: 'Pharmacy',
    items: [
      {
        path: '/pharmacy',
        icon: Pill,
        label: 'Pharmacy',
        children: [
          { path: '/pharmacy', label: 'Sales', icon: ClipboardList, group: 'Dispensing' },
          { path: '/pharmacy/drugs', label: 'Items & Stock', icon: List },
          { path: '/pharmacy/ledger', label: 'Stock Ledger', icon: ScrollText, group: 'Inventory' },
          { path: '/pharmacy/purchases', label: 'Purchase Orders', icon: ShoppingCart, group: 'Procurement' },
          { path: '/pharmacy/receipts', label: 'Goods Receipts', icon: PackageCheck },
          { path: '/pharmacy/suppliers', label: 'Suppliers', icon: Truck },
          { path: '/pharmacy/payables', label: 'Payables', icon: Wallet },
          { path: '/pharmacy/categories', label: 'Categories', icon: Tags, group: 'Catalog' },
          { path: '/pharmacy/generics', label: 'Generics', icon: FlaskConical },
          { path: '/pharmacy/companies', label: 'Companies', icon: Building2 },
          { path: '/pharmacy/racks', label: 'Racks', icon: LayoutGrid },
        ],
      },
    ],
  },
  {
    title: 'Finance',
    items: [
      {
        path: '/billing',
        icon: CreditCard,
        label: 'Billing',
        children: [
          { path: '/billing', label: 'Overview', icon: CreditCard, group: 'Summary' },
          { path: '/billing/top-sheet', label: 'Top Sheet', icon: LayoutDashboard },
          { path: '/billing/risk-fund', label: 'Risk Fund', icon: AlertTriangle },
          { path: '/billing/invoices', label: 'Invoices', icon: Receipt, group: 'Transactions' },
          { path: '/billing/payments', label: 'Payments', icon: DollarSign },
          { path: '/billing/cashbook', label: 'Cashbook', icon: BookOpen },
          { path: '/billing/payouts', label: 'Payouts', icon: Wallet, group: 'Revenue' },
        ],
      },
      // Expenses is a separate top-level entry so its own permissions —
      // `expenses:*` — control visibility without revealing the Billing menu.
      {
        path: '/billing/expenses',
        icon: Receipt,
        label: 'Expenses',
        children: [
          { path: '/billing/expenses', label: 'Ledger', icon: Receipt },
          { path: '/billing/expenses/categories', label: 'Categories', icon: Tags },
          { path: '/billing/expenses/vendors', label: 'Vendors', icon: Truck },
        ],
      },
      // Assets is a separate entry (not a Billing child) so its own
      // `assets:*` permission controls visibility without revealing Billing.
      {
        path: '/billing/assets',
        icon: Package,
        label: 'Assets',
        children: [
          { path: '/billing/assets', label: 'Asset List', icon: Package },
          { path: '/billing/assets/categories', label: 'Categories', icon: Tags },
        ],
      },
    ],
  },
  {
    title: 'Management',
    items: [
      {
        path: '/hr',
        icon: Briefcase,
        label: 'HR Mgmt.',
        children: [
          { path: '/hr', label: 'HR Mgmt.', icon: Briefcase },
          { path: '/employees', label: 'Employees', icon: UserCircle, group: 'People' },
          { path: '/doctors', label: 'Doctors', icon: Stethoscope },
          { path: '/duty-roster', label: 'Duty Roster', icon: CalendarDays },
          { path: '/hr/salary-advances', label: 'Salary Adv.', icon: HandCoins },
          { path: '/hr/payroll-policy', label: 'Payroll Policy', icon: SlidersHorizontal },
          { path: '/departments', label: 'Departments', icon: Building2, group: 'Organization' },
          { path: '/rooms', label: 'Rooms', icon: DoorOpen },
          { path: '/designations', label: 'Designations', icon: Award },
          { path: '/specializations', label: 'Specializations', icon: Stethoscope },
        ],
      },
      // One Referrals workspace: registry + payouts + vouchers + trash.
      { path: '/referrals', icon: Handshake, label: 'Referrals' },
      { path: '/reports', icon: FileText, label: 'Reports' },
      {
        path: '/roles',
        icon: Shield,
        label: 'Users & Roles',
        children: [
          { path: '/roles', label: 'Roles', icon: Shield },
          { path: '/users', label: 'Users', icon: UserCircle },
        ],
      },
      { path: '/audit', icon: ClipboardList, label: 'Audit Logs' },
      { path: '/notifications', icon: Bell, label: 'Notifications' },
    ],
  },
  {
    title: 'Website',
    items: [
      {
        path: '/website',
        icon: Globe,
        label: 'Website',
        children: [
          { path: '/website', label: 'Overview', icon: LayoutDashboard, group: 'Content' },
          { path: '/website/services', label: 'Services', icon: Tags },
          { path: '/website/blogs', label: 'Blog', icon: BookOpen },
          { path: '/website/gallery', label: 'Gallery', icon: Images },
          { path: '/website/testimonials', label: 'Testimonials', icon: MessageSquareQuote },
          { path: '/website/plans', label: 'Plans', icon: BadgeDollarSign },
          { path: '/website/taxonomy', label: 'Taxonomy', icon: FolderTree },
          { path: '/website/contact', label: 'Contact', icon: Phone },
        ],
      },
    ],
  },
  {
    title: 'System',
    items: [
      {
        path: '/settings',
        icon: Settings,
        label: 'Settings',
        children: [
          { path: '/settings/general', label: 'General', icon: Building },
          { path: '/settings/devices', label: 'Devices', icon: Monitor },
          { path: '/settings/backup', label: 'Backup', icon: Database },
          { path: '/settings/developer', label: 'Developer', icon: Terminal },
        ],
      },
    ],
  },
];

/** Open any parent whose own path or one of its descendants matches the current route. */
function computeExpandedState(sections: NavSection[], location: string): Record<string, boolean> {
  const state: Record<string, boolean> = {};
  const activeTopLevel = findActiveTopLevelLabel(sections, location);

  const walk = (items: NavItem[], isTopLevel: boolean) => {
    for (const item of items) {
      if (!item.children || item.children.length === 0) continue;
      state[item.label] = isTopLevel
        ? item.label === activeTopLevel || hasActiveDescendant(item, location)
        : hasActiveDescendant(item, location) ||
          (!!item.path && isActivePath(location, item.path));
      walk(item.children, false);
    }
  };

  for (const section of sections) walk(section.items, true);
  return state;
}

export function Sidebar({ user, onLogout, isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const { getAllowedPaths, userRoles } = usePermissions();

  const allowedPaths = getAllowedPaths();
  const activeTopLevelLabel = findActiveTopLevelLabel(staticNavSections, location.pathname);

  // A few operational screens are reserved for the environment super admin,
  // even though `admin` also holds the frontend wildcard. Hide them from
  // everyone else (the backend enforces the same rule with @Roles).
  const isSuperAdmin = userRoles.includes(UserRole.SUPER_ADMIN);
  const filterSuperAdminItems = (item: NavItem): NavItem | null => {
    if (!isSuperAdmin && item.path && SUPER_ADMIN_ONLY_PATHS.has(item.path)) {
      return null;
    }
    if (!item.children) return item;
    const children = item.children
      .map(filterSuperAdminItems)
      .filter((child): child is NavItem => child !== null);
    return { ...item, children };
  };

  const visibleSections = staticNavSections
    .map((section) => ({
      ...section,
      items: section.items
        .map(filterSuperAdminItems)
        .filter((item): item is NavItem => item !== null)
        .filter((item) => hasAllowedDescendant(item, allowedPaths)),
    }))
    .filter((section) => section.items.length > 0);

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    computeExpandedState(staticNavSections, location.pathname),
  );

  const toggle = (label: string) => {
    setExpanded((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  /** Render a submenu's children, inserting a heading whenever the group changes. */
  function renderChildren(
    children: NavItem[],
    depth: number,
    parentPath?: string,
  ): React.ReactNode[] {
    const rows: React.ReactNode[] = [];
    let currentGroup: string | undefined;

    for (const child of children.filter((c) => hasAllowedDescendant(c, allowedPaths))) {
      if (child.group && child.group !== currentGroup) {
        currentGroup = child.group;
        rows.push(
          <p
            key={`group-${depth}-${child.group}`}
            className="px-3 pt-2 pb-0.5 text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted/60"
            style={{ marginLeft: depth * 24 }}
          >
            {child.group}
          </p>,
        );
      }
      rows.push(renderNavItem(child, depth, parentPath));
    }

    return rows;
  }

  function renderNavItem(item: NavItem, depth: number = 0, parentPath?: string) {
    const children = item.children;
    const isExpanded = expanded[item.label];

    if (!children || children.length === 0) {
      return (
        <Link
          key={`${depth}-${item.label}`}
          to={item.path!}
          onClick={onClose}
          className={cn(
            'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
            (parentPath && item.path === parentPath
              ? location.pathname === item.path
              : isActivePath(location.pathname, item.path!))
              ? 'bg-primary/10 text-primary'
              : 'text-text-muted hover:bg-background hover:text-text',
          )}
          style={{ marginLeft: depth * 24 }}
        >
          <item.icon size={depth === 0 ? 18 : 16} />
          <span>{item.label}</span>
        </Link>
      );
    }

    // Top-level parents highlight based on the deepest matching entry, so a
    // sibling (e.g. Expenses under `/billing/expenses`) does not keep Billing
    // highlighted while the user is on that sibling's page.
    const isParentActive =
      depth === 0
        ? activeTopLevelLabel === item.label || hasActiveDescendant(item, location.pathname)
        : hasActiveDescendant(item, location.pathname) ||
          (!!item.path && isActivePath(location.pathname, item.path));

    return (
      <div key={`${depth}-${item.label}`} className="space-y-1">
        <button
          onClick={() => toggle(item.label)}
          className={cn(
            'flex items-center justify-between w-full gap-3 px-3 py-2 rounded-lg transition-colors',
            isParentActive ? 'text-primary' : 'text-text-muted hover:bg-background hover:text-text',
          )}
          style={{ marginLeft: depth * 24 }}
        >
          <div className="flex items-center gap-3">
            <item.icon size={depth === 0 ? 18 : 16} />
            <span>{item.label}</span>
          </div>
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        {isExpanded && (
          <div className="space-y-1">{renderChildren(children, depth + 1, item.path)}</div>
        )}
      </div>
    );
  }

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 w-64 bg-surface border-r border-border transition-transform lg:translate-x-0 lg:static flex flex-col',
        isOpen ? 'translate-x-0' : '-translate-x-full',
      )}
    >
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Activity className="w-6 h-6 text-primary" />
          <h1 className="text-xl font-bold text-primary">FastAid OS</h1>
        </div>
        <button onClick={onClose} className="lg:hidden">
          <X size={20} />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto p-4">
        {visibleSections.map((section, index) => (
          <div
            key={section.title ?? `section-${index}`}
            className={index > 0 ? 'mt-4' : undefined}
          >
            {section.title && (
              <p className="px-3 mb-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-text-muted/70">
                {section.title}
              </p>
            )}
            <div className="space-y-1">
              {section.items.map((item) => renderNavItem(item))}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-4 border-t border-border">
        <p className="px-3 mb-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-text-muted/70">
          Appearance
        </p>
        <ThemeToggle className="mb-3" />
        {user && (
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <UserCircle size={18} className="text-text-muted" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text truncate">{user.name}</p>
              <p className="text-xs text-text-muted truncate">{user.roles?.join(', ')}</p>
            </div>
          </div>
        )}
        <button
          onClick={onLogout}
          className="flex items-center gap-3 px-3 py-2 w-full text-danger hover:bg-danger/10 rounded-lg transition-colors"
        >
          <LogOut size={18} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
}
